import { useCallback, useEffect, useState } from "react";
import { getPlacements, updatePlacement } from "../../../api/ads";
import type { Placement } from "../../../api/ads";

export const usePlacements = () => {
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState<string | null>(null);

  const fetchPlacements = useCallback(async () => {
    try {
      setListLoading(true);
      setError(null);
      const response = await getPlacements({});
      setPlacements(response.results);
    } catch (err: any) {
      setError(err?.message || "Failed to load placements");
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlacements();
  }, [fetchPlacements]);

  const setPlacementEnabled = useCallback((id: string, enabled: boolean) => {
    setPlacements((prev) =>
      prev.map((p) => (p.id === id ? { ...p, enabled } : p))
    );
  }, []);

  const savePlacementConfig = useCallback(
    async (id: string, config: { frequencyCap: number; frequencyCapPerSession: boolean; maxAdvertisers: number }) => {
      setSaveLoading(id);
      setError(null);
      try {
        const updated = await updatePlacement(id, {
          frequencyCap: config.frequencyCapPerSession ? null : config.frequencyCap,
          frequencyCapPerSession: config.frequencyCapPerSession,
          maxAdvertisers: config.maxAdvertisers,
        });
        setPlacements((prev) =>
          prev.map((p) => (p.id === id ? { ...p, ...updated } : p))
        );
      } catch (err: any) {
        setError(err?.message || "Failed to save placement.");
      } finally {
        setSaveLoading(null);
      }
    },
    []
  );

  return {
    functions: {
      fetchPlacements,
      setPlacementEnabled,
      savePlacementConfig,
    },
    values: {
      placements,
      error,
    },
    loading: {
      listLoading,
      saveLoading,
    },
  };
};
